'use client';

import {
    Children,
    createContext,
    isValidElement,
    type ComponentProps,
    type CSSProperties,
    type ReactNode,
    useContext,
    useEffect,
    useState,
} from 'react';

import {
    Carousel as CarouselPrimitive,
    CarouselContent as CarouselPrimitiveContent,
    CarouselItem as CarouselPrimitiveItem,
    type CarouselApi,
} from '@/components/ui/carousel';
import { Button as ButtonUI } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Button, type ButtonVariantsProps } from '@/shared-ui/atoms/button';
import { Typography } from '@/shared-ui/atoms/typography';
import { ChevronLeftIcon } from '@/shared-ui/icons/chevron-left';
import { ChevronRightIcon } from '@/shared-ui/icons/chevron-right';

type ButtonSize = ComponentProps<typeof Button>['size'];

interface CarouselContextValue {
    api?: CarouselApi;
    setApi: (api: CarouselApi) => void;
    currentSlide: number;
    slideCount: number;
    setSlideCount: (count: number) => void;
    canScrollPrev: boolean;
    canScrollNext: boolean;
    ariaLabel?: string;
    carouselClassName?: string;
}

const CarouselContext = createContext<CarouselContextValue | null>(null);

function useCarouselContext() {
    const context = useContext(CarouselContext);

    if (!context) {
        throw new Error('Carousel compound components must be used within Carousel.Root');
    }

    return context;
}

export interface CarouselRootProps extends Omit<ComponentProps<'section'>, 'title'> {
    ariaLabel?: string;
    opts?: ComponentProps<typeof CarouselPrimitive>['opts'];
    carouselClassName?: string;
    onApiReady?: (api: CarouselApi) => void;
    onSlideChange?: (index: number) => void;
}

interface CarouselCtaProps {
    children?: ReactNode;
    variant?: Exclude<ButtonVariantsProps['variant'], 'purchase-priced'>;
    size?: ButtonSize;
}

type LayoutMode = 'contained' | 'fullwidth';

type BaseCarouselContentProps = ComponentProps<typeof CarouselPrimitiveContent> & {
    mobileLayout: LayoutMode;
    desktopLayout: LayoutMode;
    mobileGap?: number;
    desktopGap?: number;
    itemsClassName?: string;
    mobileEdgePadding?: boolean;
};

type MobileContainedContentProps = {
    mobileLayout: 'contained';
    mobileContainedSlidesPerView: number;
};

type MobileFullwidthContentProps = {
    mobileLayout: 'fullwidth';
    mobileContainedSlidesPerView?: never;
};

type DesktopContainedContentProps = {
    desktopLayout: 'contained';
    desktopContainedSlidesPerView: number;
};

type DesktopFullwidthContentProps = {
    desktopLayout: 'fullwidth';
    desktopContainedSlidesPerView?: never;
};

type CarouselContentProps = BaseCarouselContentProps &
    (MobileContainedContentProps | MobileFullwidthContentProps) &
    (DesktopContainedContentProps | DesktopFullwidthContentProps);

export interface CarouselNavigationProps {
    className?: string;
    previousClassName?: string;
    nextClassName?: string;
    iconWrapperClassName?: string;
    iconClassName?: string;
    previousIcon?: ReactNode;
    nextIcon?: ReactNode;
    /**
     * `shadow` — borderless white circle with a soft drop shadow (default). This is what the Figma
     *   carousel spec uses nearly everywhere. Delegates to the shared-ui Button `shadow` variant
     *   rather than restyling here, so the shadow value stays in one place.
     * `filled` — circular *outline* control with a background. Kept for surfaces that genuinely want
     *   a visible border; it is not the design default.
     * `unfilled` — chevron-only control with a solid background so arrows cover underlying slide content.
     */
    appearance?: 'shadow' | 'filled' | 'unfilled';
    /**
     * When true, hides the previous control when scrolled to the start and the next control
     * when scrolled to the end, instead of rendering them disabled.
     */
    hideWhenUnavailable?: boolean;
}

interface CarouselPaginationProps {
    className?: string;
    buttonClassName?: string;
    mode?: 'dots' | 'dots-with-chevrons';
}

type CarouselCssVars = CSSProperties & {
    '--carousel-contained-mobile-slides': string;
    '--carousel-contained-desktop-slides': string;
    '--carousel-mobile-gap': string;
    '--carousel-desktop-gap': string;
};

const getContainedSlidesPerView = (slidesPerView: number | undefined): number => {
    if (typeof slidesPerView !== 'number' || !Number.isFinite(slidesPerView) || slidesPerView <= 0) {
        throw new Error('Carousel.Content requires a positive number of slides when using a contained layout.');
    }

    return slidesPerView;
};

const Root = ({
    className,
    ariaLabel,
    opts,
    carouselClassName,
    onApiReady,
    onSlideChange,
    children,
    ...props
}: CarouselRootProps) => {
    // api is the Embla carousel instance, provided asynchronously by the lower-level carousel primitive.
    const [api, setApi] = useState<CarouselApi>();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slideCount, setSlideCount] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    useEffect(() => {
        if (!api) {
            return;
        }

        onApiReady?.(api);
    }, [api, onApiReady]);

    useEffect(() => {
        if (!api) {
            setCanScrollPrev(false);
            setCanScrollNext(false);
            return;
        }

        const syncScrollState = () => {
            const nextCanScrollPrev = api.canScrollPrev();
            const nextCanScrollNext = api.canScrollNext();

            setCanScrollPrev((current) => (current === nextCanScrollPrev ? current : nextCanScrollPrev));
            setCanScrollNext((current) => (current === nextCanScrollNext ? current : nextCanScrollNext));
        };

        const handleSelect = () => {
            const selectedSnap = api.selectedScrollSnap();
            setCurrentSlide(selectedSnap);
            syncScrollState();
            onSlideChange?.(selectedSnap);
        };

        handleSelect();
        api.on('select', handleSelect);
        api.on('scroll', syncScrollState);
        api.on('reInit', handleSelect);

        return () => {
            api.off('select', handleSelect);
            api.off('scroll', syncScrollState);
            api.off('reInit', handleSelect);
        };
    }, [api, onSlideChange]);

    return (
        <CarouselContext.Provider
            value={{
                api,
                setApi,
                currentSlide,
                slideCount,
                setSlideCount,
                canScrollPrev,
                canScrollNext,
                ariaLabel,
                carouselClassName,
            }}>
            <section data-slot="carousel" className={cn('space-y-6', className)} {...props}>
                <CarouselPrimitive
                    setApi={setApi}
                    opts={{ align: 'start', ...opts }}
                    className={cn('w-full', carouselClassName)}
                    aria-label={ariaLabel ?? 'Carousel'}>
                    {children}
                </CarouselPrimitive>
            </section>
        </CarouselContext.Provider>
    );
};

Root.displayName = 'Carousel.Root';

const Stage = ({ className, ...props }: ComponentProps<'div'>) => {
    return <div data-slot="carousel-stage" className={cn('relative', className)} {...props} />;
};

Stage.displayName = 'Carousel.Stage';

const Content = ({
    className,
    carouselContentClassName,
    mobileLayout,
    desktopLayout,
    desktopContainedSlidesPerView,
    mobileContainedSlidesPerView,
    mobileGap = 16,
    desktopGap = 16,
    itemsClassName,
    mobileEdgePadding = false,
    children,
    style,
    ...props
}: CarouselContentProps) => {
    const { setSlideCount, canScrollPrev, canScrollNext } = useCarouselContext();
    const slides = Children.toArray(children);

    useEffect(() => {
        setSlideCount(slides.length);
        return () => setSlideCount(0);
    }, [setSlideCount, slides.length]);

    const containedMobileSlidesPerView =
        mobileLayout === 'contained' ? getContainedSlidesPerView(mobileContainedSlidesPerView) : 1;
    const containedDesktopSlidesPerView =
        desktopLayout === 'contained' ? getContainedSlidesPerView(desktopContainedSlidesPerView) : 1;

    const cssVars: CarouselCssVars = {
        '--carousel-contained-mobile-slides': `${containedMobileSlidesPerView}`,
        '--carousel-contained-desktop-slides': `${containedDesktopSlidesPerView}`,
        '--carousel-mobile-gap': `${mobileGap}px`,
        '--carousel-desktop-gap': `${desktopGap}px`,
    };

    const mobileClasses = {
        contained:
            'basis-[calc((100%_-_((var(--carousel-contained-mobile-slides)_-_1)_*_var(--carousel-mobile-gap)))/var(--carousel-contained-mobile-slides))]',
        fullwidth: 'basis-auto',
    };

    const desktopClasses = {
        contained:
            'lg:basis-[calc((100%_-_((var(--carousel-contained-desktop-slides)_-_1)_*_var(--carousel-desktop-gap)))/var(--carousel-contained-desktop-slides))]',
        fullwidth: 'lg:grow-0 lg:basis-auto lg:w-72',
    };

    const mergedContentStyle: CSSProperties = { ...cssVars, ...style };
    const isAtEnd = !canScrollNext;

    return (
        <CarouselPrimitiveContent
            carouselContentClassName={carouselContentClassName}
            className={cn(
                'flex items-stretch !-ml-0 gap-[var(--carousel-mobile-gap)] lg:gap-[var(--carousel-desktop-gap)] select-none [&_img]:select-none [&_img]:[-webkit-user-drag:none]',
                mobileEdgePadding && !canScrollPrev && 'pl-4 lg:pl-0',
                mobileEdgePadding && canScrollPrev && 'pl-0',
                mobileEdgePadding && isAtEnd && 'pr-4 lg:pr-0',
                className
            )}
            style={mergedContentStyle}
            {...props}>
            {slides.map((slide, index) => (
                <CarouselPrimitiveItem
                    key={`slide-${String(index)}`}
                    data-slot="carousel-item"
                    style={cssVars}
                    className={cn('!pl-0', mobileClasses[mobileLayout], desktopClasses[desktopLayout], itemsClassName)}>
                    {slide}
                </CarouselPrimitiveItem>
            ))}
        </CarouselPrimitiveContent>
    );
};

Content.displayName = 'Carousel.Content';

const Heading = ({ className, variant = 'h3', ...props }: ComponentProps<typeof Typography>) => {
    return <Typography data-slot="carousel-heading" variant={variant} className={cn('mb-6', className)} {...props} />;
};

Heading.displayName = 'Carousel.Heading';

const Cta = ({ children, variant = 'default', size = 'default' }: CarouselCtaProps) => {
    const shouldUseAsChild = isValidElement(children);

    return (
        <div data-slot="carousel-cta-wrapper" className="flex justify-center mt-6">
            {shouldUseAsChild ? (
                <Button data-slot="carousel-cta" variant={variant} size={size} asChild>
                    {children}
                </Button>
            ) : (
                <Button data-slot="carousel-cta" variant={variant} size={size} type="button">
                    {children}
                </Button>
            )}
        </div>
    );
};

Cta.displayName = 'Carousel.Cta';

const Item = ({ className, ...props }: ComponentProps<'div'>) => {
    return <div className={cn('h-full', className)} {...props} />;
};

Item.displayName = 'Carousel.Item';

const filledNavigationClassName =
    'absolute top-1/2 flex -translate-y-1/2 rounded-full shadow-sm hover:!bg-background hover:!text-foreground dark:hover:!bg-input/30 dark:hover:!text-foreground';

const unfilledNavigationClassName =
    'absolute top-1/2 z-10 flex -translate-y-1/2 border-0 bg-background p-0 shadow-none text-foreground hover:bg-background hover:opacity-70 active:bg-background disabled:opacity-30';

// Positioning only — the white fill, full rounding and drop shadow come from the Button `shadow`
// variant so the shadow token is not duplicated here.
const shadowNavigationClassName = 'absolute top-1/2 z-10 flex -translate-y-1/2 p-0 disabled:opacity-30';

const Navigation = ({
    className,
    previousClassName,
    nextClassName,
    iconWrapperClassName,
    iconClassName,
    previousIcon,
    nextIcon,
    appearance = 'shadow',
    hideWhenUnavailable = false,
}: CarouselNavigationProps) => {
    const { api, slideCount, canScrollPrev, canScrollNext } = useCarouselContext();

    if (slideCount <= 1 || (!canScrollPrev && !canScrollNext)) {
        return null;
    }

    const showPrevious = hideWhenUnavailable ? canScrollPrev : true;
    const showNext = hideWhenUnavailable ? canScrollNext : true;

    const previousControl =
        appearance === 'shadow' ? (
            <Button
                type="button"
                data-slot="carousel-navigation-previous"
                variant="shadow"
                size="icon"
                disabled={!canScrollPrev}
                onClick={() => api?.scrollPrev()}
                className={cn(
                    shadowNavigationClassName,
                    'size-10',
                    iconWrapperClassName,
                    '-left-5',
                    className,
                    previousClassName
                )}>
                {previousIcon ?? <ChevronLeftIcon className={cn('size-5', iconClassName)} />}
                <span className="sr-only">Previous slide</span>
            </Button>
        ) : appearance === 'unfilled' ? (
            <Button
                type="button"
                data-slot="carousel-navigation-previous"
                variant="secondary"
                size="icon"
                disabled={!canScrollPrev}
                onClick={() => api?.scrollPrev()}
                className={cn(
                    unfilledNavigationClassName,
                    'size-10',
                    iconWrapperClassName,
                    '-left-5',
                    className,
                    previousClassName
                )}>
                {previousIcon ?? <ChevronLeftIcon className={cn('size-6', iconClassName)} />}
                <span className="sr-only">Previous slide</span>
            </Button>
        ) : (
            <ButtonUI
                type="button"
                data-slot="carousel-navigation-previous"
                variant="outline"
                size="icon"
                disabled={!canScrollPrev}
                onClick={() => api?.scrollPrev()}
                className={cn(
                    filledNavigationClassName,
                    'size-10 -left-5',
                    iconWrapperClassName,
                    className,
                    previousClassName
                )}>
                {previousIcon ?? <ChevronLeftIcon className={cn('size-5', iconClassName)} />}
                <span className="sr-only">Previous slide</span>
            </ButtonUI>
        );

    const nextControl =
        appearance === 'shadow' ? (
            <Button
                type="button"
                data-slot="carousel-navigation-next"
                variant="shadow"
                size="icon"
                disabled={!canScrollNext}
                onClick={() => api?.scrollNext()}
                className={cn(
                    shadowNavigationClassName,
                    'size-10',
                    iconWrapperClassName,
                    '-right-5',
                    className,
                    nextClassName
                )}>
                {nextIcon ?? <ChevronRightIcon className={cn('size-5', iconClassName)} />}
                <span className="sr-only">Next slide</span>
            </Button>
        ) : appearance === 'unfilled' ? (
            <Button
                type="button"
                data-slot="carousel-navigation-next"
                variant="secondary"
                size="icon"
                disabled={!canScrollNext}
                onClick={() => api?.scrollNext()}
                className={cn(
                    unfilledNavigationClassName,
                    'size-10',
                    iconWrapperClassName,
                    '-right-5',
                    className,
                    nextClassName
                )}>
                {nextIcon ?? <ChevronRightIcon className={cn('size-6', iconClassName)} />}
                <span className="sr-only">Next slide</span>
            </Button>
        ) : (
            <ButtonUI
                type="button"
                data-slot="carousel-navigation-next"
                variant="outline"
                size="icon"
                disabled={!canScrollNext}
                onClick={() => api?.scrollNext()}
                className={cn(
                    filledNavigationClassName,
                    'size-10',
                    iconWrapperClassName,
                    '-right-5',
                    className,
                    nextClassName
                )}>
                {nextIcon ?? <ChevronRightIcon className={cn('size-5', iconClassName)} />}
                <span className="sr-only">Next slide</span>
            </ButtonUI>
        );

    return (
        <>
            {showPrevious ? previousControl : null}
            {showNext ? nextControl : null}
        </>
    );
};

Navigation.displayName = 'Carousel.Navigation';

const Pagination = ({ className, buttonClassName, mode = 'dots' }: CarouselPaginationProps) => {
    const { api, currentSlide, slideCount, canScrollPrev, canScrollNext } = useCarouselContext();

    if (slideCount <= 1) {
        return null;
    }

    return (
        <div data-slot="carousel-pagination" className={cn('flex items-center justify-center gap-4 mt-6', className)}>
            {mode === 'dots-with-chevrons' ? (
                <ButtonUI
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Previous slide"
                    disabled={!canScrollPrev}
                    onClick={() => api?.scrollPrev()}
                    className="size-8 rounded-full shadow-sm hover:!bg-background hover:!text-foreground dark:hover:!bg-input/30 dark:hover:!text-foreground">
                    <ChevronLeftIcon className="size-4" />
                </ButtonUI>
            ) : null}
            <div role="tablist" aria-label="Carousel pagination" className="flex items-center justify-center gap-2">
                {Array.from({ length: slideCount }, (_, index) => {
                    const isActive = index === currentSlide;

                    return (
                        <button
                            key={index}
                            type="button"
                            role="tab"
                            aria-label={`Go to slide ${index + 1}`}
                            aria-selected={isActive}
                            className={cn(
                                'h-2 rounded-full border border-foreground transition-[width,background-color] duration-300 ease-out',
                                isActive ? 'bg-foreground w-[24px]' : 'bg-transparent w-2',
                                buttonClassName
                            )}
                            onClick={() => api?.scrollTo(index)}
                        />
                    );
                })}
            </div>
            {mode === 'dots-with-chevrons' ? (
                <ButtonUI
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Next slide"
                    disabled={!canScrollNext}
                    onClick={() => api?.scrollNext()}
                    className="size-8 rounded-full shadow-sm hover:!bg-background hover:!text-foreground dark:hover:!bg-input/30 dark:hover:!text-foreground">
                    <ChevronRightIcon className="size-4" />
                </ButtonUI>
            ) : null}
        </div>
    );
};

Pagination.displayName = 'Carousel.Pagination';

const Carousel = {
    Root,
    Stage,
    Heading,
    Cta,
    Content,
    Item,
    Navigation,
    Pagination,
} as const;

export { Carousel };
export type { CarouselApi };
